import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { LoadFailed } from "../design/LoadFailed";
import { AddButton } from "../design/AddLink";
import { StatusChip } from "../design/StatusChip";
import type { Tone } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { typedTimeHint } from "../system/clock";
import { localDateTimeInput, whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// Sweep §4 / 0182. WHAT WENT WRONG ON SITE, per event.
//
// A record is written once and never edited (the database refuses it); closing
// the follow-up is a second record with its own note. Kind, severity and state
// arrive with their labels and tones — nothing about them is spelled here.
// ---------------------------------------------------------------------------

type Incident = {
  id: string;
  kind_label: string;
  severity_label: string;
  severity_tone: Tone;
  occurred_at: string;
  description: string;
  action_taken: string | null;
  worker_name: string | null;
  recorded_by_you: boolean;
  recorded_at: string;
  state: string;
  state_label: string;
  state_tone: Tone;
  /** S70. No resolution yet, so one may be written. The server's, not a state id compared here. */
  resolvable: boolean;
  resolution_note: string | null;
  resolved_by_you: boolean | null;
  resolved_at: string | null;
  /** R13/R15b. That something was removed from the text, by whom and when — never what. */
  redactions: { label: string; by_you: boolean; at: string }[];
};

type Choice = { id: string; label: string; description?: string };
type Person = { id: string; name: string };

type Log = {
  incidents: Incident[];
  kinds: Choice[];
  severities: Choice[];
  crew: Person[];
  may_resolve: boolean;
  /** R15b. privacy.erase: may remove a bystander's name. */
  may_redact: boolean;
};

export function Incidents() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const eventId = useParams()["id"] ?? "";
  const [log, setLog] = useState<Log | null>(null);
  const [failed, setFailed] = useState<Failure | null>(null);
  const [recording, setRecording] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await send<Partial<Log> & { error?: { message?: string } }>(console_.eventIncidents(apiBase, eventId));
      if (res.status === 200 && Array.isArray(res.body.incidents)) {
        setLog(res.body as Log);
        setFailed(null);
      } else {
        // NULL, NOT AN EMPTY LOG: "nothing went wrong" and "this could not be read"
        // are opposite facts and only one of them is reassuring.
        setLog(null);
        setFailed({ status: res.status, refusal: refusal(res.body.error) });
      }
    } catch {
      setLog(null);
      setFailed({ status: 0, refusal: t("We could not reach Verbunk. Check your connection and try again.") });
    }
  }, [apiBase, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = log ? log.incidents.filter((i) => i.resolvable).length : 0;

  return (
    <div className="stack">
      <div className="panel-head">
        <h1>{t("Incidents")}</h1>
        {open > 0 ? <StatusChip tone="attention" label={t("{count} follow-up open", { count: open })} /> : null}
        {log ? <AddButton label={t("Record an incident")} expanded={recording} onClick={() => setRecording(!recording)} /> : null}
      </div>
      {failed ? <LoadFailed failure={failed} fallback={t("The incident log for this event could not be read.")} onRetry={() => void load()} /> : null}
      {log && recording ? <RecordForm log={log} eventId={eventId} orgZone={orgZone} onRecorded={async () => { await load(); setRecording(false); }} onCancel={() => setRecording(false)} /> : null}
      {log && log.incidents.length === 0 ? (
        <section className="panel">
          <p className="muted">{t("Nothing has been recorded against this event.")}</p>
        </section>
      ) : null}
      {log && log.incidents.length > 0 ? (
        <section className="panel">
          <ul className="plain-list">
            {log.incidents.map((i) => (
              <IncidentRow
                key={i.id}
                incident={i}
                mayResolve={log.may_resolve}
                mayRedact={log.may_redact}
                orgZone={orgZone}
                onResolved={load}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function RecordForm({
  log,
  eventId,
  orgZone,
  onRecorded,
  onCancel,
}: {
  log: Log;
  eventId: string;
  orgZone: string | null;
  onRecorded: () => Promise<void>;
  onCancel: () => void;
}) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [kind, setKind] = useState("");
  const [severity, setSeverity] = useState("");
  const [workerId, setWorkerId] = useState("");
  const [occurredAt, setOccurredAt] = useState(localDateTimeInput());
  const [description, setDescription] = useState("");
  const [actionTaken, setActionTaken] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(
        console_.recordIncident(apiBase, eventId, {
          kind,
          severity,
          worker_id: workerId || null,
          occurred_at: new Date(occurredAt).toISOString(),
          description,
          action_taken: actionTaken || null,
        }),
      );
      if (res.status !== 201) {
        setNotice(refusal(res.body.error) ?? t("That incident could not be recorded."));
        return;
      }
      setKind("");
      setSeverity("");
      setWorkerId("");
      setDescription("");
      setActionTaken("");
      await onRecorded();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  const kindHint = log.kinds.find((k) => k.id === kind)?.description;
  const severityHint = log.severities.find((s) => s.id === severity)?.description;

  return (
    <form
      className="panel stack"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h2>{t("Record an incident")}</h2>
      <p className="muted">
        {t("Once recorded it cannot be changed. Say what happened and what was done — not a diagnosis, and not what you think of anybody.")}
      </p>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="incident-kind">{t("Kind")}</label>
          <select id="incident-kind" required value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">{t("Choose…")}</option>
            {log.kinds.map((k) => (
              <option key={k.id} value={k.id}>
                {translateLabel(locale, k.label)}
              </option>
            ))}
          </select>
          {kindHint ? <p className="field-hint">{kindHint}</p> : null}
        </div>
        <div className="field">
          <label htmlFor="incident-severity">{t("How serious")}</label>
          <select id="incident-severity" required value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="">{t("Choose…")}</option>
            {log.severities.map((s) => (
              <option key={s.id} value={s.id}>
                {translateLabel(locale, s.label)}
              </option>
            ))}
          </select>
          {severityHint ? <p className="field-hint">{severityHint}</p> : null}
        </div>
        <div className="field">
          <label htmlFor="incident-when">{t("When it happened")}</label>
          <input
            id="incident-when"
            type="datetime-local"
            required
            value={occurredAt}
            max={localDateTimeInput()}
            onChange={(e) => setOccurredAt(e.target.value)}
          />
          <p className="field-hint">{typedTimeHint(orgZone, t)}</p>
        </div>
        <div className="field">
          <label htmlFor="incident-worker">{t("Crew member involved (optional)")}</label>
          <select id="incident-worker" value={workerId} onChange={(e) => setWorkerId(e.target.value)}>
            <option value="">{t("Nobody from the crew")}</option>
            {log.crew.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="incident-description">{t("What happened")}</label>
        <textarea
          id="incident-description"
          required
          maxLength={2000}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="incident-action">{t("What was done (optional)")}</label>
        <textarea
          id="incident-action"
          maxLength={2000}
          rows={2}
          value={actionTaken}
          onChange={(e) => setActionTaken(e.target.value)}
        />
      </div>
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not recorded")} />
          <span>{notice}</span>
        </div>
      ) : null}
      <div className="row">
        <button type="submit" className="btn" disabled={busy}>
          {busy ? t("Recording…") : t("Record incident")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onCancel}>
          {t("Cancel")}
        </button>
      </div>
    </form>
  );
}

function IncidentRow({
  incident: i,
  mayResolve,
  mayRedact,
  orgZone,
  onResolved,
}: {
  incident: Incident;
  mayResolve: boolean;
  mayRedact: boolean;
  orgZone: string | null;
  onResolved: () => Promise<void>;
}) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function resolve() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(console_.resolveIncident(apiBase, i.id, note));
      if (res.status !== 200) {
        setNotice(refusal(res.body.error) ?? t("That incident could not be closed."));
        return;
      }
      await onResolved();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="stack">
      <div className="row">
        <StatusChip tone={i.severity_tone} label={i.severity_label} />
        <strong>{translateLabel(locale, i.kind_label)}</strong>
        <StatusChip tone={i.state_tone} label={i.state_label} />
        <span className="muted">
          {whenIn(i.occurred_at, orgZone)}
          {i.worker_name ? ` · ${i.worker_name}` : ""} · {i.recorded_by_you ? t("recorded by you") : t("recorded by a colleague")}
        </span>
      </div>
      <p>{i.description}</p>
      {i.action_taken ? <p className="muted">{t("Done: {action}", { action: i.action_taken })}</p> : null}
      {i.resolution_note ? (
        <p className="muted">
          {i.resolved_by_you
            ? t("Closed {when} by you: {note}", { when: i.resolved_at ? whenIn(i.resolved_at, orgZone) : "", note: i.resolution_note })
            : t("Closed {when} by a colleague: {note}", { when: i.resolved_at ? whenIn(i.resolved_at, orgZone) : "", note: i.resolution_note })}
        </p>
      ) : null}
      {i.redactions.map((r) => (
        <p key={r.at} className="muted">
          {translateLabel(locale, r.label)} — {whenIn(r.at, orgZone)}, {r.by_you ? t("by you") : t("by a colleague")}.
        </p>
      ))}
      {mayRedact ? <RemoveName incidentId={i.id} onRemoved={onResolved} /> : null}
      {i.resolvable && mayResolve ? (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void resolve();
          }}
        >
          <label className="visually-hidden" htmlFor={`resolve-${i.id}`}>
            {t("What was done to close this")}
          </label>
          <input
            id={`resolve-${i.id}`}
            required
            maxLength={2000}
            placeholder={t("What was done to close this")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button type="submit" className="btn btn-quiet" disabled={busy || note.trim() === ""}>
            {busy ? t("Closing…") : t("Mark resolved")}
          </button>
        </form>
      ) : null}
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not closed")} />
          <span>{notice}</span>
        </div>
      ) : null}
    </li>
  );
}

/**
 * R15b. Remove a bystander's name from this record. In-page and with a second
 * click (the DiscardDraft pattern), because the words are gone afterwards: the
 * database keeps only a mark that something was removed, by whom and when.
 */
function RemoveName({ incidentId, onRemoved }: { incidentId: string; onRemoved: () => Promise<void> }) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function remove() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(console_.redactIncidentName(apiBase, incidentId, name));
      if (res.status !== 200) {
        setNotice(refusal(res.body.error) ?? t("That name could not be removed."));
        setConfirming(false);
        return;
      }
      setName("");
      setConfirming(false);
      setOpen(false);
      await onRemoved();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="row">
        <button type="button" className="btn btn-quiet" onClick={() => setOpen(true)}>
          {t("Remove a name from this record")}
        </button>
      </div>
    );
  }
  return (
    <div className="stack">
      <div className="row">
        <label className="visually-hidden" htmlFor={`redact-${incidentId}`}>
          {t("The name to remove, as it is written in the record")}
        </label>
        <input
          id={`redact-${incidentId}`}
          maxLength={120}
          placeholder={t("The name, as it is written")}
          value={name}
          disabled={confirming}
          onChange={(e) => setName(e.target.value)}
        />
        {confirming ? (
          <>
            <span>{t("Remove every “{name}” from this record? It cannot be put back.", { name: name.trim() })}</span>
            <button type="button" className="btn" disabled={busy} onClick={() => void remove()}>
              {busy ? t("Removing…") : t("Yes, remove it")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setConfirming(false)}>
              {t("Keep it")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-quiet"
              disabled={name.trim().length < 2}
              onClick={() => setConfirming(true)}
            >
              {t("Remove")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setOpen(false)}>
              {t("Cancel")}
            </button>
          </>
        )}
      </div>
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not removed")} />
          <span>{notice}</span>
        </div>
      ) : null}
    </div>
  );
}
